import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, EnvelopeSimple, Phone, Cake, Ruler, Target, Note, FloppyDisk,
  Check, X as XIcon, CalendarBlank, Lightning, MapPin,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";

function fmtPct(p) {
  if (p == null) return "—";
  return `${p.toFixed(1)}%`;
}

function StatTile({ label, value, accent = "default", testid }) {
  const accents = {
    default: "text-white",
    good: "text-emerald-400",
    bad: "text-[#FF6B61]",
    info: "text-brand-light",
  };
  return (
    <div className="card-flat px-5 py-4 flex flex-col gap-1" data-testid={testid}>
      <div className="tracker text-zinc-500 text-[10px]">{label}</div>
      <div className={`font-display text-3xl leading-none ${accents[accent] || accents.default}`}>{value}</div>
    </div>
  );
}

export default function TrainerClientProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesDirty, setNotesDirty] = useState(false);
  const [locations, setLocations] = useState([]);
  const [defaultLoc, setDefaultLoc] = useState("");
  const [savingLoc, setSavingLoc] = useState(false);
  const [pendingCredit, setPendingCredit] = useState(0);
  const [creditInput, setCreditInput] = useState("");
  const [creditSaving, setCreditSaving] = useState(false);
  const [editingCredit, setEditingCredit] = useState(false);

  const load = async () => {
    try {
      const [prof, locs, credits] = await Promise.all([
        api.get(`/clients/${id}/profile`),
        api.get(`/locations`).catch(() => ({ data: [] })),
        api.get(`/clients/${id}/session-credits`).catch(() => ({ data: { pending_credit: 0 } })),
      ]);
      setData(prof.data);
      setNotes(prof.data.profile?.trainer_notes || "");
      setNotesDirty(false);
      setLocations(locs.data || []);
      setDefaultLoc(prof.data.profile?.default_location || "");
      setPendingCredit(credits.data?.pending_credit || 0);
      setCreditInput(String(credits.data?.pending_credit ?? 0));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load profile");
    }
  };
  useEffect(() => { load(); }, [id]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.patch(`/clients/${id}/trainer-notes`, { trainer_notes: notes });
      toast.success("Notes saved");
      setNotesDirty(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSavingNotes(false);
    }
  };

  const saveDefaultLoc = async (newVal) => {
    setSavingLoc(true);
    try {
      await api.patch(`/clients/${id}`, { default_location: newVal });
      setDefaultLoc(newVal);
      toast.success(newVal ? `Default location set to ${newVal}` : "Default location cleared");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSavingLoc(false);
    }
  };

  const saveCredit = async () => {
    const num = Number(creditInput);
    if (!Number.isFinite(num) || num < 0) {
      toast.error("Credit must be 0 or more");
      return;
    }
    setCreditSaving(true);
    try {
      const { data: res } = await api.put(`/clients/${id}/session-credits`, { amount: num, reason: "trainer_override" });
      setPendingCredit(res.pending_credit);
      setCreditInput(String(res.pending_credit));
      setEditingCredit(false);
      toast.success("Next-month credit updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setCreditSaving(false);
    }
  };

  if (!data) return <div className="tracker text-zinc-500">Loading…</div>;
  const { profile, stats } = data;

  const fmtDate = (iso) => iso ? new Date(`${iso}T12:00:00Z`).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—";
  const ageFrom = (dob) => {
    if (!dob) return null;
    const d = new Date(`${dob}T12:00:00Z`);
    const diff = Date.now() - d.getTime();
    return Math.floor(diff / (365.25 * 24 * 3600 * 1000));
  };
  const age = ageFrom(profile.date_of_birth);

  return (
    <div className="space-y-8" data-testid="trainer-client-profile">
      <div>
        <Link to="/trainer/clients" className="tracker text-zinc-500 hover:text-white inline-flex items-center gap-2 text-[11px]" data-testid="back-to-clients">
          <ArrowLeft size={12} weight="bold" /> Back to clients
        </Link>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start gap-6">
        <div className="shrink-0">
          {profile.profile_image_b64 ? (
            <img src={profile.profile_image_b64} alt={profile.name} className="w-28 h-28 object-cover border border-white/10" data-testid="profile-photo" />
          ) : (
            <div className="w-28 h-28 bg-[#1A1A1A] border border-white/10 flex items-center justify-center font-display text-4xl text-zinc-600" data-testid="profile-photo-placeholder">
              {(profile.name || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="tracker text-brand mb-1">[ CLIENT PROFILE ]</div>
          <h1 className="font-display text-4xl sm:text-5xl" data-testid="profile-name">{profile.name}</h1>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-zinc-400">
            <div className="inline-flex items-center gap-1.5"><EnvelopeSimple size={12} weight="bold" className="text-zinc-500" /> <span className="font-mono">{profile.email}</span></div>
            {profile.phone && (
              <div className="inline-flex items-center gap-1.5" data-testid="profile-phone"><Phone size={12} weight="bold" className="text-zinc-500" /> <span className="font-mono">{profile.phone}</span></div>
            )}
            {profile.date_of_birth && (
              <div className="inline-flex items-center gap-1.5" data-testid="profile-dob"><Cake size={12} weight="bold" className="text-zinc-500" /> {fmtDate(profile.date_of_birth)}{age != null && <span className="text-zinc-600">· {age}y</span>}</div>
            )}
            {profile.height_cm && (
              <div className="inline-flex items-center gap-1.5" data-testid="profile-height"><Ruler size={12} weight="bold" className="text-zinc-500" /> {profile.height_cm}cm</div>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <section>
        <div className="tracker text-zinc-500 mb-3">[ ATTENDANCE STATS ]</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <StatTile label="Attended" value={stats.attended} accent="good" testid="stat-attended" />
          <StatTile label="Cancelled" value={stats.cancelled} accent="bad" testid="stat-cancelled" />
          <StatTile label="Upcoming" value={stats.upcoming} accent="info" testid="stat-upcoming" />
          <StatTile label="Lifetime" value={stats.lifetime} testid="stat-lifetime" />
          <StatTile label="Attendance %" value={fmtPct(stats.attendance_rate)} accent={stats.attendance_rate != null && stats.attendance_rate >= 80 ? "good" : stats.attendance_rate != null && stats.attendance_rate < 50 ? "bad" : "default"} testid="stat-rate" />
        </div>
        {stats.attendance_rate == null && (
          <div className="tracker text-[10px] text-zinc-600 mt-2">% shown once at least one session has been completed or cancelled.</div>
        )}
      </section>

      {/* Next-month credit override */}
      <section data-testid="pending-credit-section">
        <div className="tracker text-zinc-500 mb-3">[ NEXT-MONTH BILL ADJUSTMENT ]</div>
        <div className="card-flat p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-sm text-zinc-300">
              Pending credit to deduct from next month's invoice:{" "}
              <span className="font-mono font-bold text-emerald-400 money" data-testid="pending-credit-value">
                {pendingCredit.toFixed(2)}
              </span>
            </div>
            <div className="tracker text-[10px] text-zinc-600 mt-1">
              Auto-accrued from cancellations, holidays, and no-show sessions. Override manually if you've credited the client differently.
            </div>
          </div>
          {editingCredit ? (
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                step="0.01"
                value={creditInput}
                onChange={(e) => setCreditInput(e.target.value)}
                className="w-28 px-3 py-2 border bg-[#0A0A0A] font-mono"
                data-testid="pending-credit-input"
              />
              <button onClick={saveCredit} disabled={creditSaving} className="btn-primary !py-2 !px-3 !text-xs disabled:opacity-40" data-testid="pending-credit-save">
                {creditSaving ? "Saving…" : "Save"}
              </button>
              <button onClick={() => { setEditingCredit(false); setCreditInput(String(pendingCredit)); }} className="btn-ghost !py-2 !px-3 !text-xs">Cancel</button>
            </div>
          ) : (
            <button onClick={() => setEditingCredit(true)} className="btn-ghost !py-2 !px-4 !text-xs" data-testid="pending-credit-edit">
              Edit
            </button>
          )}
        </div>
      </section>

      {/* Goals */}
      {profile.goals && (
        <section data-testid="profile-goals-section">
          <div className="tracker text-zinc-500 mb-3"><Target size={12} weight="duotone" className="inline mr-1" /> [ GOALS ]</div>
          <div className="card-flat p-5 whitespace-pre-wrap text-sm text-zinc-300">{profile.goals}</div>
        </section>
      )}

      {/* Default location */}
      <section>
        <div className="tracker text-zinc-500 mb-3">
          <MapPin size={12} weight="duotone" className="inline mr-1" /> [ DEFAULT LOCATION ]
        </div>
        <div className="card-flat p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <select
            value={defaultLoc}
            onChange={(e) => saveDefaultLoc(e.target.value)}
            disabled={savingLoc}
            className="flex-1 px-3 py-2 border bg-[#0A0A0A] text-sm w-full sm:w-auto"
            data-testid="default-location-select"
          >
            <option value="">— None (pick per session) —</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.name}>{loc.name}</option>
            ))}
            {defaultLoc && !locations.some((l) => l.name === defaultLoc) && (
              <option value={defaultLoc}>{defaultLoc} (removed)</option>
            )}
          </select>
          <div className="tracker text-[10px] text-zinc-600 flex-1">
            Pre-fills every new booking for this client — override on any single session as needed.
          </div>
        </div>
      </section>

      {/* Trainer-only notes */}
      <section>
        <div className="tracker text-zinc-500 mb-3 flex items-center justify-between">
          <span><Note size={12} weight="duotone" className="inline mr-1" /> [ TRAINER-ONLY NOTES ]</span>
          {profile.trainer_notes_updated_at && (
            <span className="text-[10px] text-zinc-600">Updated {new Date(profile.trainer_notes_updated_at).toLocaleDateString()}</span>
          )}
        </div>
        <div className="card-flat p-4 space-y-3">
          <textarea
            value={notes}
            onChange={(e) => { setNotes(e.target.value); setNotesDirty(true); }}
            placeholder="Private notes about this client — injuries, preferences, anything they won't see…"
            rows={5}
            className="w-full px-3 py-2 border bg-[#0A0A0A] text-sm font-mono"
            data-testid="trainer-notes-textarea"
            maxLength={5000}
          />
          <div className="flex items-center justify-between">
            <div className="tracker text-[10px] text-zinc-600">Only visible to you, not the client.</div>
            <button
              onClick={saveNotes}
              disabled={!notesDirty || savingNotes}
              className="btn-primary !py-2 !px-4 !text-xs disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="save-trainer-notes"
            >
              <FloppyDisk size={12} weight="bold" /> {savingNotes ? "Saving…" : notesDirty ? "Save notes" : "Saved"}
            </button>
          </div>
        </div>
      </section>

      {/* Quick links */}
      <section>
        <div className="tracker text-zinc-500 mb-3">[ QUICK ACTIONS ]</div>
        <div className="flex flex-wrap gap-3">
          <Link to="/trainer/schedule" className="btn-ghost !py-2 !px-4 !text-xs" data-testid="link-schedule"><CalendarBlank size={12} weight="bold" /> Calendar</Link>
          <Link to="/trainer/clients" className="btn-ghost !py-2 !px-4 !text-xs" data-testid="link-back-clients"><Lightning size={12} weight="bold" /> Session plan</Link>
        </div>
      </section>
    </div>
  );
}
